import List, { ListItem } from '../list/list.component'
import NeiModal from './nei-modal.component'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  CurecallBadge,
  CurecallButton,
  ECurecallBadgeColor,
  ECurecallButtonAppearance,
} from '@honestica/curecall-ui'
import Card from 'src/components/card/card.component'
import { TPatientVariable } from 'src/types/variable'
import { TPatientForm } from 'src/types/form'

interface NeiCardProps {
  NeiForm: TPatientForm
}

export interface NeiVariable extends ListItem {
  defaultLabel: string
  nonScoringVariables: ListItem[]
}

const stripHtml = (html: string) => {
  const doc = new DOMParser().parseFromString(html, 'text/html')

  return doc.body.textContent || ''
}

export type TNeiVariableGroup = {
  patientAnswer: string
  value: string | number
  gridCenterIsHit?: boolean
  VariableId: number
  PatientFormId: number
  name: string
  label: string
  group: string
  isScoringVariable?: boolean
}

export const pleaseDeleteMe = (patientsVariables?: TPatientVariable[]) => {
  return patientsVariables
    ?.map((patientVariable: TPatientVariable) => ({
      patientAnswer: patientVariable?.patientAnswer,
      value: patientVariable?.value,
      gridCenterIsHit: patientVariable?.gridCenterIsHit,
      VariableId: patientVariable?.VariableId,
      PatientFormId: patientVariable?.VariableId,
      name: patientVariable?.Variable?.name,
      label: patientVariable?.Variable?.label,
      group: patientVariable?.Variable?.group?.toLowerCase()?.replace('vision specific:', '')?.trim(),
      isScoringVariable: patientVariable?.Variable?.settings?.isScoringVariable,
    }))
    .reduce((r: any, a: any) => {
      r[a?.group] = r[a?.group] || []
      r[a?.group].push(a)
      return r
    }, Object.create(null))
}

const NeiCard = ({ NeiForm }: NeiCardProps) => {
  const { t } = useTranslation()
  const [isOpenNeiModal, setIsOpenNeiModal] = useState(false)
  const neiVariables: NeiVariable[] = useMemo(() => {
    const items: NeiVariable[] = []
    const variables = pleaseDeleteMe(NeiForm.PatientVariables) ?? {}

    for (const [key, value] of Object.entries(variables)) {
      const groupVariables = value as TNeiVariableGroup[]
      const scoringVariable = groupVariables.find((variable) => variable.isScoringVariable)

      if (scoringVariable) {
        items.push({
          label: t(key.replace(' ', '-')),
          defaultLabel: key,
          nonScoringVariables: groupVariables
            .filter((variable) => !variable.isScoringVariable)
            .map((variable) => {
              return {
                label: stripHtml(variable.label),
                value: (
                  <div className="c-flex c-w-1/4 c-items-center c-justify-center">
                    <CurecallBadge
                      color={ECurecallBadgeColor.SMOOTH_PRIMARY}
                      className="tw-text-sm tw-px-2 tw-text-center"
                    >
                      {variable.patientAnswer}
                    </CurecallBadge>
                  </div>
                ),
              }
            }),
          value: isNaN(Number.parseInt(scoringVariable.patientAnswer)) ? (
            <p className="c-m-0">
              {t('components.patient.patient-file.body.followup-details.not-concerned')}
            </p>
          ) : (
            <CurecallBadge color={ECurecallBadgeColor.SMOOTH_PRIMARY} className="tw-text-sm tw-px-2">
              {parseInt(scoringVariable.patientAnswer, 10)}%
            </CurecallBadge>
          ),
        })
      }
    }

    return items || []
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [NeiForm])

  const toggleNeiModal = () => setIsOpenNeiModal((prev) => !prev)

  return (
    <>
      <NeiModal isOpen={isOpenNeiModal} neiVariables={neiVariables} onToggle={toggleNeiModal} />
      <Card>
        <div className="c-flex c-justify-between c-items-center">
          <h3 className="c-font-bold">
            {t('components.patient.patient-file.body.followup-details.nei-title')}
          </h3>
          <CurecallButton appearance={ECurecallButtonAppearance['PRIMARY-OUTLINED']} onClick={toggleNeiModal}>
            {t('components.patient.patient-file.body.followup-details.score-detail')}
          </CurecallButton>
        </div>
        <div className="c-mt-10 c-mb-4">
          {neiVariables ? (
            <List list={neiVariables} ordered />
          ) : (
            <div className="text-muted">
              {t('components.patient.patient-file.body.followup-details.form-sent-but-not-filled-yet')}
            </div>
          )}
        </div>
      </Card>
    </>
  )
}

export default NeiCard
